// The calendar. Three views, three different jobs — and one board you can actually work on.
//
// ── WHY THEY ARE NOT THE SAME SCREEN THREE TIMES ────────────────────────────────
// The first version rendered month as a date grid and week and day as the same lane grid with a
// different number of columns, so "Dag" was a week view with six columns missing. A planner switching
// views expects the QUESTION to change, not the width:
//
//   Maand — OVERZICHT. Where are we tight, which werven run when. One row per week, no per-person
//           detail, a team load figure under every date. You read it, you do not plan in it.
//   Week  — PLANNEN. One row per person, seven columns, everything droppable, everything resizable.
//           This is the working surface, and the only view optimised for the gesture over the glance.
//   Dag   — DETAIL. One card per person with the actual jobs: hours, werf, discipline, note, status.
//           What a foreman reads out at the morning briefing.
//
// ── WHAT MAKES IT FEEL LIKE A TOOL ──────────────────────────────────────────────
// Selection, a command catalogue, and undo. Nothing here implements an action: `lib/commands.ts` owns
// what the actions ARE, `lib/selection.ts` owns what they act ON, and this file owns the surfaces —
// keyboard, right-click, drag, buttons — that dispatch into them. That separation is why the same
// eleven actions answer to four different gestures without eleven times four handlers.
//
// ── WHAT A DROP ACTUALLY SENDS ──────────────────────────────────────────────────
// Moving to another day is `PUT …/plan` with the revision the chip was rendered with. Moving to another
// PERSON is two guarded writes — assign, then withdraw — because `uq_wa_active_pair` makes "reassign" a
// supersede-then-assign by construction. Assign runs FIRST so a refusal cannot leave a job with nobody
// on it. They are not atomic and the UI says so on failure.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as A from '../api/planning.ts';
import { ConflictError, ForbiddenError, UnauthorizedError } from '../api/client.ts';
import { keys, correlationId, invalidatePlanning } from '../lib/mutate.ts';
import {
  addDays, dayLabel, dayNumber, daysBetween, endOfMonth, hours, isWeekend, longLabel, monthGrid, monthLabel,
  range, startOfMonth, startOfWeek, today, type Ymd,
} from '../lib/dates.ts';
import { CapacityIndex, fmt, loadLevel } from '../lib/capacity.ts';
import { mark, measure } from '../lib/perf.ts';
import { identityColour } from '../lib/colour.ts';
import { useDragController, type DragMode, type DragPayload, type DropTarget } from '../lib/drag.ts';
import {
  EMPTY_SELECTION, clickSelect, days as selectedDays, isSelected, prune, resolve, selKey, selectAll,
  workpackages, type Selection,
} from '../lib/selection.ts';
import { COMMANDS, matchKey, pasteOnto, type CmdCtx, type CmdResult, type Command } from '../lib/commands.ts';
import { ContextMenu, type MenuAt } from '../ui/Menu.tsx';
import { Chip } from '../ui/Chip.tsx';
import { InlineEdit } from '../ui/InlineEdit.tsx';
import { AddProject } from './AddProject.tsx';
import { SplitDialog, type SplitTarget } from '../ui/SplitDialog.tsx';
import { bulk, bulkMessage } from '../lib/bulk.ts';
import { Avatar, Banner, CapacityPill, Spinner } from '../ui/bits.tsx';
import { WorkpackagePanel, type PanelSubject } from './WorkpackagePanel.tsx';

type View = 'month' | 'week' | 'day';

/** `#week/2026-08-17` — the view and the day, parsed defensively because a URL is user input. */
function readHash(): { view?: View; day?: Ymd } {
  if (typeof window === 'undefined') return {};
  const m = /^#(month|week|day)\/(\d{4}-\d{2}-\d{2})$/.exec(window.location.hash);
  return m ? { view: m[1] as View, day: m[2] } : {};
}

/**
 * The one line under a name: ploeg, firm, trades — and, when it applies, that this person is not
 * booked alone.
 *
 * ── WHY THE SUPERVISION MARKER IS WORDED LIKE THIS ──────────────────────────────
 * The source behind these people explains WHY each rule exists, in language nobody should read off a
 * planning board. "Werkt in duo" is the whole of what a planner needs: it tells them what to do when
 * they drag this person onto a job, and it says nothing about the person. The server refuses the
 * booking either way — this is the courtesy of saying so before the click, not the enforcement.
 */
function CrewLine({ c, trades = 3 }: { c: A.CrewMember | null; trades?: number }) {
  if (!c) return null;
  // `trades={0}` MEANS "not on the line, still in the tooltip". The week view is 170px of name column
  // and the trade list turned every lane head into four wrapped rows with the name ellipsised above
  // them — trading the one thing that view exists to show for a list you can read in the day view.
  if (trades === 0) {
    const bits = [...new Set([c.team, c.externalOrg].filter(Boolean) as string[])];
    return (
      <span className="crew-line faint">
        {bits.length > 0 && <span title={[...bits, A.tradeSummary(c.skills, 99).full].filter(Boolean).join(' · ')}>{bits.join(' · ')}</span>}
        {c.needsSupervision && <span className="badge duo" title="Wordt niet alleen ingepland">duo</span>}
        {!c.fieldCapacity && <span className="badge ro" title="Plant en begeleidt, voert zelf niet uit">leiding</span>}
      </span>
    );
  }
  // THE TEAM NAME AND THE FIRM ARE THE SAME WORD for the hired-in crew — the ploeg is named after the
  // firm they come from. Printing "KTMAX · KTMAX" says nothing twice, so the pair is de-duplicated
  // rather than the data being changed to suit the line.
  const bits = [...new Set([c.team, c.externalOrg].filter(Boolean) as string[])];
  const t = A.tradeSummary(c.skills, trades);
  // ONE TEXT RUN, not a text node beside a span. Split across two elements the flex row wrapped
  // between them on a phone, and the second line then opened with a bare "·" — a separator with
  // nothing in front of it. Joined here, the wrap falls inside the sentence where it belongs.
  const line = [...bits, t.shown && `${t.shown}${t.hidden > 0 ? ` +${t.hidden}` : ''}`]
    .filter(Boolean).join(' · ');
  return (
    <span className="crew-line faint">
      {line && <span title={[...bits, t.full].filter(Boolean).join(' · ')}>{line}</span>}
      {c.needsSupervision && <span className="badge duo" title="Wordt niet alleen ingepland">werkt in duo</span>}
      {!c.fieldCapacity && <span className="badge ro" title="Plant en begeleidt, voert zelf niet uit">leiding</span>}
    </span>
  );
}

export function Calendar({ me }: { me: A.WhoAmI }) {
  const qc = useQueryClient();
  /**
   * THE VIEW AND THE DATE SURVIVE A REFRESH, because losing your place is the fastest way to make a
   * tool feel disposable. A planner who reloads while looking at the week of 17 August wants that
   * week back, not today in whatever view the app prefers.
   *
   * Kept in the URL rather than in storage: it makes the page shareable ("kijk eens naar deze week"),
   * the back button works, and nothing is written to a device somebody else may pick up.
   *
   * WEEK ON A DESK, DAY ON A PHONE, but only as the FIRST guess. A seven-column lane grid on a 375px
   * screen shows two days and squeezes the name column to "Mo…", so the phone opens on the day list;
   * once somebody has chosen, their choice wins.
   */
  const [view, setView] = useState<View>(() => {
    const h = readHash();
    if (h.view) return h.view;
    return typeof window !== 'undefined' && window.innerWidth <= 640 ? 'day' : 'week';
  });
  const [anchor, setAnchor] = useState<Ymd>(() => readHash().day ?? today());

  useEffect(() => {
    const next = `#${view}/${anchor}`;
    if (window.location.hash !== next) window.history.replaceState(null, '', next);
  }, [view, anchor]);

  const [personFilter, setPersonFilter] = useState<string>('');
  const [teamFilter, setTeamFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [notice, setNotice] = useState<{ kind: 'err' | 'warn' | 'info'; text: string; undo?: () => Promise<void> } | null>(null);
  const [editing, setEditing] = useState<PanelSubject | null>(null);
  /** Which capacity figure the planner asked "why" about. One at a time; clicking again closes it. */
  const [why, setWhy] = useState<{ resourceId: number; day: Ymd; name: string } | null>(null);
  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION);
  const [menu, setMenu] = useState<MenuAt | null>(null);
  const [clipboard, setClipboard] = useState<A.ScheduleItem[]>([]);
  const [busy, setBusy] = useState(false);
  /** The day the pointer last touched — where ⌘V puts things. */
  const hoverDay = useRef<Ymd>(anchor);

  const [from, to] = useMemo<[Ymd, Ymd]>(() => {
    if (view === 'month') { const g = monthGrid(anchor); return [g[0], g[g.length - 1]]; }
    if (view === 'week') { const s = startOfWeek(anchor); return [s, addDays(s, 6)]; }
    return [anchor, anchor];
  }, [view, anchor]);

  const schedule = useQuery({
    queryKey: keys.schedule(from, to, personFilter || undefined),
    queryFn: () => A.getSchedule(from, to, personFilter || undefined),
  });
  const people = useQuery({ queryKey: keys.people, queryFn: A.listPeople, staleTime: 60_000 });
  const teams = useQuery({ queryKey: keys.teams, queryFn: A.listTeams, staleTime: 60_000 });
  const projects = useQuery({ queryKey: keys.projects, queryFn: A.listProjects, staleTime: 60_000 });
  const crew = useQuery({ queryKey: keys.crew, queryFn: A.listCrew, staleTime: 60_000 });

  /** Deployment facts by actor, so a lane can say ploeg and vak without a second lookup per render. */
  const crewBy = useMemo(() => {
    const m = new Map<string, A.CrewMember>();
    for (const c of crew.data ?? []) m.set(c.actorId, c);
    return m;
  }, [crew.data]);

  const items = useMemo(() => {
    const all = schedule.data?.items ?? [];
    return projectFilter ? all.filter((i) => String(i.projectId) === projectFilter) : all;
  }, [schedule.data, projectFilter]);

  // A SELECTION MUST NOT OUTLIVE ITS ROWS. After a delete or a refetch the keys are re-checked against
  // what actually came back, so a command can never fire at a bar that is no longer there.
  useEffect(() => { setSelection((s) => prune(s, items)); }, [items]);

  const selected = useMemo(() => resolve(selection, items), [selection, items]);

  // ONE capacity implementation for all three views. See lib/capacity.ts for why that matters.
  const cap = useMemo(() => new CapacityIndex(schedule.data), [schedule.data]);

  /** Screen order, for shift-click. Whatever the current view lays out, top to bottom, left to right. */
  const orderedKeys = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const i of [...items].sort((a, b) => a.day.localeCompare(b.day) || a.resourceId - b.resourceId)) {
      const k = selKey(i);
      if (!seen.has(k)) { seen.add(k); out.push(k); }
    }
    return out;
  }, [items]);

  // ── how a failure is explained ────────────────────────────────────────────────
  const explain = useCallback((e: unknown, what: string): { kind: 'err' | 'warn'; text: string } => {
    if (e instanceof ConflictError) {
      return { kind: 'warn', text: `Iemand anders heeft ${what} ondertussen gewijzigd. Het scherm is vernieuwd; probeer opnieuw.` };
    }
    if (e instanceof UnauthorizedError) {
      return { kind: 'err', text: 'Je sessie is verlopen. Meld je opnieuw aan; de wijziging is niet doorgevoerd.' };
    }
    if (e instanceof ForbiddenError) {
      return { kind: 'err', text: `Je mag ${what} niet wijzigen. Er is niets veranderd.` };
    }
    // The server's reason is the substance — which window, which phase, or which rule this crew is
    // planned around. Shown verbatim behind a Dutch lead-in, because dropping a planner into an
    // English sentence reads as a crash.
    return { kind: 'err', text: `${what} is niet gewijzigd — de planning weigert het: ${(e as Error).message}` };
  }, []);

  const ctx: CmdCtx = useMemo(() => ({
    selection: selected, all: items, anchor, clipboard, setClipboard,
    openPanel: setEditing, crew: crew.data ?? [], people: people.data ?? [],
  }), [selected, items, anchor, clipboard, crew.data, people.data]);

  /**
   * Run a command and report what happened.
   *
   * EVERY WRITE PATH GOES THROUGH HERE — keyboard, menu, button — so the busy state, the refetch, the
   * error wording and the undo offer are written once. When a second one appears, that is the bug.
   */
  const run = useCallback(async (cmd: Command | (() => Promise<CmdResult>), what = 'de selectie') => {
    if (busy) return;
    setBusy(true);
    try {
      const r = typeof cmd === 'function' ? await cmd() : await cmd.run(ctx);
      if (r.message || r.undo) {
        setNotice({ kind: 'info', text: r.message ?? 'Gedaan.', undo: r.undo });
      } else {
        setNotice(null);
      }
    } catch (e) {
      setNotice(explain(e, what));
      // RECORDED WITHOUT THE SERVER'S MESSAGE. A refusal sentence can name a person's competency, and
      // the decision log is read by people who must never see that. The CODE says what kind of
      // refusal it was, which is all any later analysis needs.
      A.recordEvent({
        eventType: 'planner_suggestion_rejected', source: 'manual', screen: view, accepted: false,
        rejectionCode: e instanceof ConflictError ? 'conflict'
          : e instanceof ForbiddenError ? 'forbidden'
          : e instanceof UnauthorizedError ? 'unauthorized' : 'refused',
        projectId: selected[0]?.projectId ?? null,
        workpackageId: selected[0]?.workpackageId ?? null,
      });
    } finally {
      setBusy(false);
      // Invalidate every project the selection touched — a bulk move can span werven.
      for (const p of new Set(selected.map((i) => i.projectId))) invalidatePlanning(qc, p);
      qc.invalidateQueries({ queryKey: keys.projects });
    }
  }, [busy, ctx, explain, qc, selected]);

  const undoLast = useCallback(async () => {
    const u = notice?.undo;
    if (!u) return;
    setNotice(null);
    setBusy(true);
    try {
      await u();
      // AN UNDO IS THE MOST INFORMATIVE EVENT IN THE LOG: it marks a decision somebody took back,
      // which is exactly what a later suggestion must not repeat.
      A.recordEvent({ eventType: 'undo_performed', source: 'manual', screen: view });
      setNotice({ kind: 'info', text: 'Ongedaan gemaakt.' });
    } catch (e) {
      setNotice(explain(e, 'het ongedaan maken'));
    } finally {
      setBusy(false);
      for (const p of new Set(selected.map((i) => i.projectId))) invalidatePlanning(qc, p);
    }
  }, [notice, explain, qc, selected]);

  // ── the drop ──────────────────────────────────────────────────────────────────

  const onDrop = async (p: DragPayload, t: DropTarget, mode: DragMode) => {
    // ── RESIZE ────────────────────────────────────────────────────────────────
    // The edge follows the day under the cursor. Dragging the end before the start (or the start past
    // the end) is a gesture, not an error — it is clamped to a one-day job, which is what the shape
    // under the finger already looked like.
    if (mode !== 'move') {
      if (!p.plannedStart || !p.plannedEnd) return;
      const start = mode === 'resize-start' ? (t.day > p.plannedEnd ? p.plannedEnd : t.day) : p.plannedStart;
      const end = mode === 'resize-end' ? (t.day < p.plannedStart ? p.plannedStart : t.day) : p.plannedEnd;
      if (start === p.plannedStart && end === p.plannedEnd) return;
      await run(async () => {
        const r = await A.setWorkpackageWindow(p.projectId, p.workpackageId, {
          start, end, reason: 'aangepast in de kalender',
          expectedRevision: p.planRevision, correlationId: correlationId(),
        });
        return {
          message: `"${p.title}" duurt nu ${daysBetween(start, end) + 1} ${daysBetween(start, end) === 0 ? 'dag' : 'dagen'}.`,
          undo: async () => {
            await A.setWorkpackageWindow(p.projectId, p.workpackageId, {
              start: p.plannedStart!, end: p.plannedEnd!, reason: 'ongedaan gemaakt',
              expectedRevision: r.revision, correlationId: correlationId(),
            });
          },
        };
      }, `"${p.title}"`);
      return;
    }

    // ── MOVE ONE DAY ──────────────────────────────────────────────────────────
    // A chip IS a day now, so this moves that day and nothing else. Dropping Monday on Thursday
    // leaves Tuesday exactly where it was — the whole point of migration 033, and the thing the old
    // code could not do because it only ever knew the workpackage window.
    //
    // DRAGGING ONE OF SEVERAL SELECTED DAYS MOVES THEM ALL by the same offset, so a staggered week
    // keeps its shape. Grabbing a chip outside the selection means the planner changed their mind
    // about what they are holding.
    const key = selKey({ workpackageId: p.workpackageId, resourceId: p.resourceId!, day: p.day });
    const inSelection = selection.keys.has(key);
    if (!inSelection) setSelection({ keys: new Set([key]), anchor: key });

    const moving = inSelection && selected.length > 1
      ? selectedDays(selected)
      : items.filter((i) => i.workpackageId === p.workpackageId
                         && i.resourceId === p.resourceId && i.day === p.day);
    const sameDay = p.day === t.day;
    const sameResource = t.resourceId === null || t.resourceId === p.resourceId;
    if (sameDay && sameResource) return;
    const delta = sameDay ? 0 : daysBetween(p.day, t.day);

    // ── OPTIMISTIC, THEN CONFIRMED ────────────────────────────────────────────
    // The cache is patched before the request leaves, so the chip lands under the finger at once
    // instead of after a round trip. `snapshot` is the exact state to put back, which is what makes
    // a refusal a clean bounce rather than a reload.
    const snapshot = qc.getQueryData(keys.schedule(from, to, personFilter || undefined));
    qc.setQueryData(keys.schedule(from, to, personFilter || undefined), (old: A.Schedule | undefined) => {
      if (!old) return old;
      const movingKeys = new Set(moving.map((m) => `${m.assignmentId}|${m.day}`));
      return {
        ...old,
        items: old.items.map((i) => (movingKeys.has(`${i.assignmentId}|${i.day}`)
          ? { ...i, day: addDays(i.day, delta),
              resourceId: t.resourceId ?? i.resourceId,
              resourceName: t.resourceId && t.resourceId !== i.resourceId
                ? (lanes.find((l) => l.resourceId === t.resourceId)?.name ?? i.resourceName)
                : i.resourceName }
          : i)),
      };
    });

    mark('drag-optimistic');
    measure('drag:drop-optimistic', 'drag-drop', 'drag-optimistic');

    setBusy(true);
    try {
      // ── PARTIAL PROCESSING, WITH A RESULT PER DAY ────────────────────────────
      // The contract is stated once in lib/bulk.ts and chosen deliberately: nineteen good moves are
      // not undone because the twentieth was refused, and the refused ones are NAMED rather than
      // counted. See that file for why atomicity was rejected.
      const r = await bulk(moving, async (m) => {
        await A.moveAllocation(m.projectId, {
          assignmentId: m.assignmentId, day: m.day,
          ...(delta !== 0 ? { toDay: addDays(m.day, delta) } : {}),
          ...(t.resourceId && t.resourceId !== m.resourceId ? { toResourceId: t.resourceId } : {}),
          screen: view, correlationId: correlationId(),
        });
        const landedOn = addDays(m.day, delta);
        const wasResource = m.resourceId, proj = m.projectId, wp = m.workpackageId;
        return async () => {
          // The inverse looks the day up by its NEW position, because that is where the row is now.
          const back = await A.getSchedule(from, to, personFilter || undefined);
          const row = back.items.find((x) => x.day === landedOn && x.workpackageId === wp
            && x.resourceId === (t.resourceId ?? wasResource));
          if (!row) return;
          await A.moveAllocation(proj, {
            assignmentId: row.assignmentId, day: landedOn,
            ...(delta !== 0 ? { toDay: m.day } : {}),
            ...(t.resourceId && t.resourceId !== wasResource ? { toResourceId: wasResource } : {}),
            screen: view, correlationId: correlationId(),
          });
        };
      });

      mark('drag-server');
      measure('drag:drop-server', 'drag-drop', 'drag-server');

      if (r.failed.length > 0) {
        // A PARTIAL RESULT IS NOT A SUCCESS MESSAGE. The optimistic patch moved everything, so the
        // rows that were refused are showing in the wrong place until the refetch below corrects
        // them — which is exactly why this says so rather than staying quiet.
        setNotice({ kind: 'warn', text: bulkMessage(r, (i) => `${(i as A.ScheduleItem).resourceName} op ${(i as A.ScheduleItem).day}`), undo: r.undo });
        for (const f of r.failed) {
          A.recordEvent({
            eventType: 'planner_suggestion_rejected', source: 'drag', screen: view, accepted: false,
            rejectionCode: 'refused',
            projectId: (f.item as A.ScheduleItem).projectId,
            workpackageId: (f.item as A.ScheduleItem).workpackageId,
          });
        }
      } else {
        setNotice({
          kind: 'info',
          text: moving.length > 1 ? `${moving.length} dagen verplaatst.`
            : `"${p.title}" — ${hours(p.minutes)} verplaatst naar ${longLabel(t.day)}.`,
          undo: r.undo,
        });
      }
    } catch (e) {
      // Only reachable when something outside the per-item loop failed — a dropped session, say.
      qc.setQueryData(keys.schedule(from, to, personFilter || undefined), snapshot);
      setNotice(explain(e, `"${p.title}"`));
    } finally {
      setBusy(false);
      // ONE targeted refetch after the server has spoken, not the six `invalidatePlanning` fires.
      // The optimistic patch already shows the right thing; this only reconciles what the server
      // decided (a merge, a materialisation, a new assignment id).
      void qc.invalidateQueries({ queryKey: ['schedule'], refetchType: 'active' });
    }
  };

  const drag = useDragController(onDrop);

  /**
   * Open the panel on THIS row.
   *
   * ── WHY THIS DOES NOT GO THROUGH THE `open` COMMAND ─────────────────────────
   * It used to: a double-click set the selection and then dispatched `open`, which reads
   * `ctx.selection[0]`. But `ctx` is memoised from the RESOLVED selection, and that has not
   * recomputed yet inside the same handler — so the command read the previous selection, which on a
   * fresh page is empty, and threw "Cannot read properties of undefined (reading 'workpackageId')"
   * into the planner's face.
   *
   * Opening a chip is about THAT CHIP. Routing it through the selection was indirection that could
   * only ever be wrong.
   */
  const openItem = useCallback((i: A.ScheduleItem) => {
    const crewOf = new Map<number, { resourceId: number; hours: number; assignmentId: number | null }>();
    for (const r of items.filter((x) => x.workpackageId === i.workpackageId)) {
      crewOf.set(r.resourceId, {
        resourceId: r.resourceId, hours: r.allocatedMinutes / 60, assignmentId: r.assignmentId });
    }
    setEditing({
      projectId: i.projectId, projectCode: i.projectCode, projectName: i.projectName,
      officialNumber: i.officialNumber, customerName: i.customerName,
      workpackageId: i.workpackageId, title: i.title, scope: i.scope,
      disciplineCode: i.disciplineCode, lifecycleState: i.lifecycleState,
      plannedStart: i.plannedStart, plannedEnd: i.plannedEnd,
      planRevision: i.planRevision, assignees: [...crewOf.values()],
    });
  }, [items]);

  /** "Nieuwe opdracht" — the same panel, empty, defaulted to the day you were looking at. */
  const openNew = (day?: Ymd, resourceId?: number) => setEditing({
    projectId: projectFilter ? Number(projectFilter) : (projects.data?.[0]?.projectId ?? null),
    workpackageId: null, title: '', scope: null, lifecycleState: 'draft',
    plannedStart: day ?? anchor, plannedEnd: day ?? anchor, planRevision: 0,
    assignees: resourceId ? [{ resourceId, hours: 8, assignmentId: null }] : [],
  });

  // ── the keyboard ──────────────────────────────────────────────────────────────
  //
  // ONE LISTENER, DISPATCHING INTO THE SAME CATALOGUE the menu renders. A shortcut cannot therefore do
  // something the menu does not offer, or offer something the menu spells differently.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      // NEVER STEAL A KEY FROM A FIELD. ⌘A in the notes box selects the note, not the whole week, and
      // Delete in an hours input deletes a digit. Without this the panel became unusable the moment
      // shortcuts existed.
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
      if (editing) return;

      if (e.key === 'Escape') { setSelection(EMPTY_SELECTION); setMenu(null); return; }
      if (matchKey(e, 'mod+a')) { e.preventDefault(); setSelection(selectAll(orderedKeys)); return; }
      if (matchKey(e, 'mod+z')) { e.preventDefault(); void undoLast(); return; }
      if (matchKey(e, 'mod+v')) {
        e.preventDefault();
        void run(() => pasteOnto(ctx, hoverDay.current), 'het plakken');
        return;
      }
      // View switching. Single letters, unmodified — the way every calendar does it.
      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        if (e.key === 'm') { setView('month'); return; }
        if (e.key === 'w') { setView('week'); return; }
        if (e.key === 'd' && !e.shiftKey) { setView('day'); return; }
        if (e.key === 't') { setAnchor(today()); return; }
        if (e.key === 'n') { e.preventDefault(); openNew(hoverDay.current); return; }
        // Paging the calendar when nothing is selected: the arrows belong to the view, not to a job.
        if (selected.length === 0 && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          const step = view === 'month' ? 30 : view === 'week' ? 7 : 1;
          setAnchor((a) => addDays(a, e.key === 'ArrowRight' ? step : -step));
          return;
        }
      }
      for (const c of COMMANDS) {
        if (c.keys && matchKey(e, c.keys)) {
          if (!c.enabled(ctx)) return;
          e.preventDefault();
          void run(c);
          return;
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ctx, editing, orderedKeys, run, selected.length, undoLast, view]);

  // ── selection gestures on a chip ──────────────────────────────────────────────

  const chipClick = (i: A.ScheduleItem, e: React.MouseEvent) => {
    setSelection((s) => clickSelect(s, selKey(i), { shift: e.shiftKey, meta: e.metaKey || e.ctrlKey }, orderedKeys));
  };

  const chipContext = (i: A.ScheduleItem, e: React.MouseEvent) => {
    e.preventDefault();
    // RIGHT-CLICKING OUTSIDE THE SELECTION REPLACES IT. Right-clicking inside it keeps it, so "select
    // five, right-click one, move them all" works — which is the whole reason multi-select exists.
    if (!isSelected(selection, i)) setSelection({ keys: new Set([selKey(i)]), anchor: selKey(i) });
    setMenu({ x: e.clientX, y: e.clientY });
  };

  /**
   * LONG-PRESS IS THE RIGHT-CLICK OF A PHONE.
   *
   * Without it every command in the catalogue is desktop-only: there is no second mouse button on a
   * building site, and the selection bar alone cannot carry "toewijzen aan …". 450ms is long enough
   * not to fire while scrolling and short enough not to feel broken; the timer is cancelled by any
   * movement past the drag threshold, so a press that becomes a drag never also opens a menu.
   */
  const press = useRef<{ t: number; x: number; y: number } | null>(null);
  const chipPressStart = (i: A.ScheduleItem, e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    const { clientX: x, clientY: y } = e;
    const t = window.setTimeout(() => {
      if (!isSelected(selection, i)) setSelection({ keys: new Set([selKey(i)]), anchor: selKey(i) });
      setMenu({ x, y });
      // A menu that opens under the finger must not also leave a drag armed behind it.
      drag.cancel();
      if (navigator.vibrate) navigator.vibrate(8);
    }, 450);
    press.current = { t, x, y };
  };
  const chipPressEnd = () => { if (press.current) { clearTimeout(press.current.t); press.current = null; } };
  const chipPressMove = (e: React.PointerEvent) => {
    const p = press.current;
    if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 8) chipPressEnd();
  };

  /** Assigning the selection to somebody, straight from the menu. */
  const assignTo = (resourceId: number, name: string) => run(async () => {
    const targets = workpackages(selected);
    const undos: Array<() => Promise<void>> = [];
    for (const i of targets) {
      const created = await A.assign(i.projectId, {
        workpackageId: i.workpackageId, resourceId, allocatedHours: i.allocatedMinutes / 60,
        reason: 'toegewezen via het menu', correlationId: correlationId(),
      });
      undos.push(async () => { await A.unassign(i.projectId, created.id, correlationId()); });
    }
    return {
      message: `${name} toegevoegd aan ${targets.length === 1 ? `"${targets[0].title}"` : `${targets.length} taken`}.`,
      undo: async () => { for (const u of undos.reverse()) await u(); },
    };
  }, 'de toewijzing');

  /** "Opsplitsen" on a specific day. The catalogue's split opens the panel for a whole workpackage;
      this one is about the DAY under the cursor, which is what a planner right-clicks. */
  const splitExtra = useMemo(() => {
    const one = selected.length === 1 ? selected[0] : null;
    if (!one || one.minutes < 30) return [];
    return [{
      group: 'edit',
      label: `Dag opsplitsen (${hours(one.minutes)})`,
      onPick: () => setSplitting({
        projectId: one.projectId, assignmentId: one.assignmentId, workpackageId: one.workpackageId,
        day: one.day as Ymd, minutes: one.minutes, title: one.title, resourceId: one.resourceId,
      }),
    }];
  }, [selected]);

  const menuExtra = useMemo(() => {
    if (selected.length === 0) return [];
    const already = new Set(selected.map((i) => i.resourceId));
    return (people.data ?? [])
      .filter((p) => p.resourceId !== null && !already.has(p.resourceId))
      .slice(0, 8)
      .map((p) => {
        const info = crewBy.get(p.actorId);
        return {
          group: 'crew',
          label: `Toewijzen aan ${p.displayName}${info?.needsSupervision ? ' (werkt in duo)' : ''}`,
          onPick: () => void assignTo(p.resourceId!, p.displayName),
        };
      });
  }, [selected, people.data, crewBy, assignTo]);

  /**
   * EVERY ACTIVE PERSON AND TEAM GETS A LANE, not only those who already have work.
   *
   * A person with a free week has no schedule rows — and a row is the drop target. Without this you
   * cannot give work to the one person who is available, which is the single most common thing a
   * planner does on a Monday morning. An empty lane is the answer to "who can take this".
   */
  const lanes = useMemo(() => {
    type Lane = { name: string; isTeam: boolean; actorId: string | null };
    const byResource = new Map<number, Lane>();
    for (const p of people.data ?? []) {
      if (p.resourceId !== null) byResource.set(p.resourceId, { name: p.displayName, isTeam: false, actorId: p.actorId });
    }
    for (const t of teams.data ?? []) {
      if (t.resourceId !== null) byResource.set(t.resourceId, { name: t.name, isTeam: true, actorId: null });
    }
    // Anybody carrying work who is somehow not in the register still gets a lane — their work must not
    // vanish because their resource row was closed.
    for (const i of items) {
      if (!byResource.has(i.resourceId)) {
        byResource.set(i.resourceId, { name: i.resourceName ?? '—', isTeam: i.isTeam, actorId: i.actorId });
      }
    }
    const all = [...byResource.entries()].map(([id, v]) => ({
      resourceId: id, ...v, crew: v.actorId ? crewBy.get(v.actorId) ?? null : null,
    }));
    const wanted = personFilter ? people.data?.find((p) => p.actorId === personFilter)?.resourceId ?? null : null;
    return all
      .filter((l) => (wanted === null ? true : l.resourceId === wanted))
      // BY PLOEG, because that is how the work is handed out: "wie is er vrij in ploeg 2". A team
      // resource has no ploeg of its own and is kept out of a ploeg-filtered list rather than shown
      // under a heading it does not belong to.
      .filter((l) => (teamFilter === '' ? true : l.crew?.team === teamFilter))
      .sort((a, b) => Number(a.isTeam) - Number(b.isTeam) || a.name.localeCompare(b.name));
  }, [items, people.data, teams.data, personFilter, teamFilter, crewBy]);

  const laneIds = useMemo(() => lanes.map((l) => l.resourceId), [lanes]);
  const days = useMemo(() => (view === 'month' ? monthGrid(anchor) : range(from, to)), [view, anchor, from, to]);
  const step = view === 'month' ? 30 : view === 'week' ? 7 : 1;

  /**
   * EVERY OVERLOADED CELL IN THE PERIOD, as a list somebody can work through.
   *
   * A red cell tells a planner something is wrong where they happen to be looking. It does not tell
   * them how many there are, or that the worst one is three rows below the fold. On a week with forty
   * jobs that is the difference between "the board has colours" and "the board has a to-do list" —
   * and the point of a capacity warning is that somebody goes and fixes it.
   *
   * Computed from the same `CapacityIndex` every cell renders from, so this list and the colours can
   * never disagree. That is the entire reason that class exists.
   */
  const problems = useMemo(() => {
    const out: { resourceId: number; name: string; day: Ymd; over: number }[] = [];
    for (const lane of lanes) {
      for (const d of days) {
        const c = cap.at(lane.resourceId, d);
        if (c.overloadMinutes > 0) out.push({ resourceId: lane.resourceId, name: lane.name, day: d, over: c.overloadMinutes });
      }
    }
    return out.sort((a, b) => b.over - a.over);
  }, [lanes, days, cap]);
  const [showProblems, setShowProblems] = useState(false);
  /** Which month cells the planner has expanded. In-place, so it never navigates away. */
  const [expanded, setExpanded] = useState<ReadonlySet<Ymd>>(new Set());
  const [addingProject, setAddingProject] = useState(false);
  const [splitting, setSplitting] = useState<SplitTarget | null>(null);
  /** Which day the phone opened as a bottom sheet. Never navigates — the month stays behind it. */
  const [sheet, setSheet] = useState<Ymd | null>(null);

  const title = view === 'month' ? monthLabel(anchor)
    : view === 'day' ? longLabel(anchor)
    : `${longLabel(from)} — ${longLabel(to)}`;

  /** Everything a chip needs to take part in selection, drag, resize and the menu. */
  const chipProps = (i: A.ScheduleItem, resizable = false) => ({
    item: i,
    dragProps: drag.dragHandleProps,
    resizeProps: resizable ? drag.resizeHandleProps : undefined,
    selected: isSelected(selection, i),
    onSelect: chipClick,
    onOpen: (x: A.ScheduleItem) => { setSelection({ keys: new Set([selKey(x)]), anchor: selKey(x) }); openItem(x); },
    onContext: chipContext,
    onPressStart: chipPressStart,
    onPressEnd: chipPressEnd,
    onPressMove: chipPressMove,
  });

  return (
    <div className="stack" onPointerDown={(e) => {
      // CLICKING THE BACKGROUND CLEARS THE SELECTION — but only the background. Without the target
      // check, every click on a chip cleared the selection a moment after making it.
      const el = e.target as HTMLElement;
      if (!el.closest('.chip') && !el.closest('.menu') && !el.closest('.job')) setSelection(EMPTY_SELECTION);
    }}>
      {/* ── the toolbar. Two rows on a phone, one on a desk; see styles.css. ────── */}
      <div className="toolbar">
        <div className="tabs" role="tablist" aria-label="Weergave">
          {(['month', 'week', 'day'] as View[]).map((v) => (
            <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)}
                    title={`${v === 'month' ? 'Maand' : v === 'week' ? 'Week' : 'Dag'} (${v[0]})`}>
              {v === 'month' ? 'Maand' : v === 'week' ? 'Week' : 'Dag'}
            </button>
          ))}
        </div>
        <div className="row nav">
          <button className="btn small" onClick={() => setAnchor(addDays(anchor, -step))} aria-label="Vorige">‹</button>
          <button className="btn small" onClick={() => setAnchor(today())} title="Vandaag (t)">Vandaag</button>
          <button className="btn small" onClick={() => setAnchor(addDays(anchor, step))} aria-label="Volgende">›</button>
        </div>
        <strong className="period">{title}</strong>
        {/* THE COUNT IS THE POINT. A planner should not have to scroll a grid to discover how much is
            wrong with it — and when nothing is wrong the button is absent rather than reassuring,
            because a permanent "0 problemen" is furniture nobody reads after the first day. */}
        {problems.length > 0 && (
          <button className="btn small problems" onClick={() => setShowProblems((v) => !v)}
                  aria-expanded={showProblems}>
            {problems.length} {problems.length === 1 ? 'overbezetting' : 'overbezettingen'}
          </button>
        )}
        <span className="spacer" />
        {/* ONE STRIP, not three stacked rows. See `.filters` in styles.css for why a sideways scroll
            is the right answer here and nowhere else. */}
        <div className="filters">
        <select className="btn small" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} aria-label="Filter op ploeg">
          <option value="">Alle ploegen</option>
          {[...new Set((crew.data ?? []).map((c) => c.team).filter((t): t is string => !!t))].sort()
            .map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="btn small" value={personFilter} onChange={(e) => setPersonFilter(e.target.value)} aria-label="Filter op medewerker">
          <option value="">Alle medewerkers</option>
          {(people.data ?? []).filter((p) => p.resourceId !== null)
            .map((p) => <option key={p.actorId} value={p.actorId}>{p.displayName}</option>)}
        </select>
        <select className="btn small" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} aria-label="Filter op werf">
          <option value="">Alle werven</option>
          {(projects.data ?? []).map((p) => (
            <option key={p.projectId} value={String(p.projectId)}>{A.werfLabel(p).name}</option>
          ))}
        </select>
        {/* ADDING A WERF IS A PLANNER'S JOB, not a seed's. Until now the only werven on the board were
            the ones somebody had seeded; there was no way to put a new one on it at all. */}
        <button className="btn small" onClick={() => setAddingProject(true)} title="Werf toevoegen aan de planner">
          + Werf
        </button>
        </div>
      </div>

      {/* ── THE SELECTION BAR. Appears only when something is held, so the toolbar above stays the
          same shape all day and this reads as a mode rather than as more furniture. */}
      {selected.length > 0 && (
        <div className="selbar" role="toolbar" aria-label="Selectie">
          <strong>{workpackages(selected).length} geselecteerd</strong>
          {COMMANDS.filter((c) => ['day-earlier', 'day-later', 'next-week', 'shorter', 'longer', 'copy', 'duplicate', 'unassign'].includes(c.id))
            .map((c) => (
              <button key={c.id} className={`btn small${c.danger ? ' danger' : ''}`} disabled={!c.enabled(ctx) || busy}
                      onClick={() => void run(c)} title={c.label(ctx)}>
                {SHORT[c.id] ?? c.label(ctx)}
              </button>
            ))}
          <span className="spacer" />
          <button className="btn small" onClick={() => setSelection(EMPTY_SELECTION)}>Selectie wissen (Esc)</button>
        </div>
      )}

      {showProblems && problems.length > 0 && (
        <div className="card problems-list">
          <header className="row">
            <strong>Wie staat er te vol</strong>
            <span className="faint">{view === 'day' ? 'deze dag' : view === 'week' ? 'deze week' : 'deze maand'}</span>
            <span className="spacer" />
            <button className="btn small" onClick={() => setShowProblems(false)}>Sluiten</button>
          </header>
          <ul className="why-list">
            {problems.slice(0, 12).map((p) => (
              <li key={`${p.resourceId}-${p.day}`}>
                {/* CLICKING A PROBLEM TAKES YOU TO IT. A list of complaints you then have to find by
                    hand is a worse experience than no list: this jumps to the day and opens the same
                    breakdown the capacity pill opens, which is where the fix actually happens. */}
                <button className="linkish" onClick={() => {
                  setAnchor(p.day);
                  setView('day');
                  setWhy({ resourceId: p.resourceId, day: p.day, name: p.name });
                  setShowProblems(false);
                }}>
                  {p.name} · {longLabel(p.day)}
                </button>
                <strong className="over-text">{fmt(p.over)} u te veel</strong>
              </li>
            ))}
            {problems.length > 12 && (
              <li className="faint"><span>en nog {problems.length - 12} andere</span><span /></li>
            )}
          </ul>
        </div>
      )}

      {notice && (
        <Banner kind={notice.kind}>
          {notice.text}
          {notice.undo && (
            <button className="btn small" style={{ marginLeft: 'auto' }} onClick={() => void undoLast()}>
              Ongedaan maken (⌘Z)
            </button>
          )}
        </Banner>
      )}
      {schedule.isError && <Banner kind="err">Planning kon niet geladen worden: {(schedule.error as Error).message}</Banner>}
      {drag.state.viaKeyboard && drag.state.payload && (
        <Banner kind="info">
          «{drag.state.payload.title}» opgenomen. Pijltjestoetsen verplaatsen, Enter zet neer, Esc annuleert.
          {drag.state.over && ` → ${longLabel(drag.state.over.day)}`}
        </Banner>
      )}

      {schedule.isPending ? <Spinner /> : view === 'month' ? (
        // ══ MAAND — OVERZICHT ════════════════════════════════════════════════════
        // No lanes, no per-person rows: the question here is "which weeks are tight and what runs
        // when". Every date carries the TEAM load, so a full month is one glance rather than N glances.
        // ══ MAAND — OVERZICHT ════════════════════════════════════════════════════
        // Rebuilt. It used to show three chips and hide the rest behind "+8 andere", which is exactly
        // the information a month view exists to give: WHO is where, and where it is tight. Clicking
        // through to another screen to find out defeats the point of looking at a month.
        //
        // Now: compact one-line entries — person, werf, number, hours — as many as fit, and the
        // overflow EXPANDS THE CELL IN PLACE. No navigation, and the cell folds back.
        <div className="cal-grid month" role="grid" aria-label="Maandoverzicht">
          {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((d) => <div key={d} className="cal-head">{d}</div>)}
          {days.map((d) => {
            const inMonth = d >= startOfMonth(anchor) && d <= endOfMonth(anchor);
            const dayItems = items.filter((i) => i.day === d)
              .sort((a, b) => (a.resourceName ?? '').localeCompare(b.resourceName ?? ''));
            const c = cap.total(laneIds, [d]);
            const over = drag.state.over?.day === d && drag.state.over.resourceId === null;
            const open = expanded.has(d);
            const shown = open ? dayItems : dayItems.slice(0, MONTH_ROWS);
            return (
              <div key={d} data-drop-day={d}
                   className={`cal-day${isWeekend(d) ? ' nonwork' : ''}${inMonth ? '' : ' other-month'}${d === today() ? ' today' : ''}${over ? ' drop-target' : ''}${open ? ' open' : ''}`}
                   {...drag.dropTargetProps({ day: d, resourceId: null })} tabIndex={-1} role="gridcell"
                   onPointerEnter={() => { hoverDay.current = d; }}
                   onDoubleClick={(e) => { if (!(e.target as HTMLElement).closest('.mrow')) openNew(d); }}>
                <div className="cal-daynum">
                  <span>{dayNumber(d)}</span>
                  {c.capacityMinutes > 0 && <CapacityPill c={c} compact />}
                </div>
                {shown.map((i) => {
                  const w = A.werfLabel(i);
                  return (
                    <button key={`${i.assignmentId}-${i.day}`}
                            className={`mrow${isSelected(selection, i) ? ' selected' : ''}`}
                            style={{ borderLeftColor: identityColour(String(i.resourceId), i.isTeam).border }}
                            onClick={(e) => { e.stopPropagation(); chipClick(i, e); }}
                            onDoubleClick={(e) => { e.stopPropagation(); setSelection({ keys: new Set([selKey(i)]), anchor: selKey(i) }); openItem(i); }}
                            onContextMenu={(e) => chipContext(i, e)}
                            title={`${i.resourceName} · ${w.name} · ${w.code ?? ''} · ${hours(i.minutes)}`}>
                      <span className="m-who">{i.resourceName}</span>
                      <span className="m-werf">{w.name}</span>
                      <span className="m-nr">{w.number ?? ''}</span>
                      <span className="m-u">{hours(i.minutes)}</span>
                    </button>
                  );
                })}
                {/* PHONE ONLY (see styles.css): the lines are hidden at that width because they
                    cannot be read, so this carries the cell. Four facts a planner scans a month for:
                    how much is planned, how many jobs, is it over, and is there room left. */}
                <button className={`m-count lvl-${loadLevel(c.assignedMinutes, c.capacityMinutes)}`}
                        onClick={(e) => { e.stopPropagation(); setSheet(d); }}>
                  {/* THE PILL ABOVE ALREADY SAYS "23,5 / 48" — planned AND capacity, coloured by
                      load — so free hours and overload are both readable from it. Repeating them as
                      words in a 53px column produced three broken lines with "u" wrapping away from
                      its number. This adds the one fact the pill does not carry. */}
                  {dayItems.length === 0
                    ? <span className="faint">vrij</span>
                    : <span>{dayItems.length} {dayItems.length === 1 ? 'taak' : 'taken'}</span>}
                </button>
                {dayItems.length > MONTH_ROWS && (
                  // IN PLACE, NOT ELSEWHERE. The cell grows and shrinks; the month stays the month.
                  <button className="more" onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(d)) next.delete(d); else next.add(d);
                      return next;
                    });
                  }}>
                    {open ? 'minder tonen' : `+${dayItems.length - MONTH_ROWS} meer tonen`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : view === 'week' ? (
        // ══ WEEK — PLANNEN ═══════════════════════════════════════════════════════
        // The working surface. One row per person, every cell a drop target, every chip resizable from
        // either edge, every cell showing "6 / 8 u" so the answer to "can he take this" is on screen
        // before the drag starts.
        <div className="lane-scroll">
          <div className="lanes" style={{ gridTemplateColumns: `minmax(120px, 170px) repeat(${days.length}, minmax(104px, 1fr))` }}>
            <div className="lane-corner" />
            {days.map((d) => (
              <div key={d} className={`cal-head${d === today() ? ' today' : ''}`}>
                {dayLabel(d)} {dayNumber(d)}
              </div>
            ))}
            {lanes.length === 0 && <div className="faint" style={{ gridColumn: '1 / -1', padding: 16 }}>
              Geen medewerkers in het register.
            </div>}
            {lanes.map((lane) => {
              const week = cap.total([lane.resourceId], days);
              return (
                <div className="lane-row" key={lane.resourceId} style={{ display: 'contents' }}>
                  <div className="lane-head">
                    <Avatar id={String(lane.resourceId)} name={lane.name} isTeam={lane.isTeam} />
                    {/* NAME OVER META, IN ONE COLUMN. Laid out as siblings of the avatar they were
                        all flex items of one wrapping row, and the meta line's full-width basis ate
                        the name's: every lane in the week view rendered its ploeg and trades with
                        NO NAME above them. The same two-part block the day card uses. */}
                    <div className="lane-who">
                      <span className="lane-name" title={lane.name}>{lane.name}</span>
                      <span className="lane-meta">
                        {/* PLOEG AND THE MARKERS ONLY. The trades stay in the tooltip and in the day
                            view; here they cost the name its width and the head its height. */}
                        <CrewLine c={lane.crew} trades={0} />
                        {/* The week total sits on this line rather than in its own column, so the
                            name above it gets the whole 170px instead of two thirds of it. */}
                        <span className="faint lane-total">{fmt(week.assignedMinutes)}/{fmt(week.capacityMinutes)} u</span>
                      </span>
                    </div>
                  </div>
                  {days.map((d) => {
                    const cell = items.filter((i) => i.day === d && i.resourceId === lane.resourceId);
                    const c = cap.at(lane.resourceId, d);
                    const over = drag.state.over?.day === d && drag.state.over.resourceId === lane.resourceId;
                    return (
                      <div key={`${lane.resourceId}-${d}`} data-drop-day={d} data-drop-resource={lane.resourceId}
                           className={`lane-cell${c.nonWorking ? ' nonwork' : ''}${over ? ' drop-target' : ''} lvl-${loadLevel(c.assignedMinutes, c.capacityMinutes)}`}
                           {...drag.dropTargetProps({ day: d, resourceId: lane.resourceId })} tabIndex={-1}
                           onPointerEnter={() => { hoverDay.current = d; }}
                           onDoubleClick={(e) => { if (!(e.target as HTMLElement).closest('.chip')) openNew(d, lane.resourceId); }}>
                        {/* ── A CELL HAS A CEILING ──────────────────────────────
                            With a real week on the board a busy Monday holds five jobs, and rendering
                            all of them made one lane four times the height of its neighbours: the grid
                            stopped being scannable in exactly the situation a planner most needs to
                            scan it. Three, then a count. The overflow is not hidden — it is one click
                            to the day view, which is the screen built for reading a full day anyway. */}
                        {cell.slice(0, CELL_MAX).map((i) => (
                          <Chip key={i.assignmentId} {...chipProps(i, true)}
                                spanStart={i.day === i.plannedStart} spanEnd={i.day === i.plannedEnd} />
                        ))}
                        {cell.length > CELL_MAX && (
                          <button className="more" onClick={(e) => { e.stopPropagation(); setAnchor(d); setView('day'); }}>
                            +{cell.length - CELL_MAX} meer
                          </button>
                        )}
                        {!c.nonWorking && (
                          // CLICKING THE PILL SAYS WHY. "Capaciteit moet visueel zijn … ik wil
                          // onmiddellijk zien groen oranje rood EN WAAROM." The colour is the glance;
                          // this is the answer to the glance.
                          <button className="pill-btn"
                                  onClick={() => setWhy(why?.resourceId === lane.resourceId && why.day === d
                                    ? null : { resourceId: lane.resourceId, day: d, name: lane.name })}>
                            <CapacityPill c={c} />
                          </button>
                        )}
                        {c.otherMinutes > 0 && (
                          // ANONYMOUS BY DESIGN: work on a project this planner cannot see. Shown so
                          // they do not double-book a real person; unattributed so it discloses nothing.
                          <span className="faint" title="Werk op een werf die je niet kan zien">
                            + {hours(c.otherMinutes)} elders
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // ══ DAG — DETAIL ═════════════════════════════════════════════════════════
        // One card per person, with the things a week cell has no room for: the note, the status, the
        // customer, the full task name. This is the briefing view, and it is also the only view that
        // reads well on a phone held one-handed, which is where a foreman actually opens it.
        <div className="day-list">
          {lanes.length === 0 && <p className="faint">Geen medewerkers in het register.</p>}
          {lanes.map((lane) => {
            const mine = items.filter((i) => i.day === anchor && i.resourceId === lane.resourceId);
            const c = cap.at(lane.resourceId, anchor);
            return (
              <section key={lane.resourceId} className={`card day-card lvl-${loadLevel(c.assignedMinutes, c.capacityMinutes)}`}
                       data-drop-day={anchor} data-drop-resource={lane.resourceId}
                       {...drag.dropTargetProps({ day: anchor, resourceId: lane.resourceId })}
                       onPointerEnter={() => { hoverDay.current = anchor; }}>
                <header className="day-card-head">
                  <Avatar id={String(lane.resourceId)} name={lane.name} isTeam={lane.isTeam} />
                  <div className="day-card-who">
                    <strong>{lane.name}</strong>
                    {/* A full-width card, not a 170px lane — so it can afford more of the list. */}
                    <CrewLine c={lane.crew} trades={6} />
                  </div>
                  <span className="spacer" />
                  <CapacityPill c={c} />
                </header>
                {/* WORK FIRST, ALWAYS. The empty-state used to be chosen before the jobs were looked
                    at, so a person with zero capacity and real work — exactly the operational lead,
                    who is booked to SUPERVISE — got "geen werkdag" printed over a day he was on a
                    werf. Whether somebody has hours to spare and whether somebody has work are two
                    different questions, and only the second one may hide a job. */}
                {mine.length === 0 && (
                  <p className="faint" style={{ margin: 0 }}>
                    {/* TWO DIFFERENT ZEROES. A Sunday is a non-working day; a lead is a person whose
                        day is not made of werf hours. Printing "geen werkdag" under his name on a
                        Tuesday was plainly false. */}
                    {c.nonWorking
                      ? (lane.crew && !lane.crew.fieldCapacity
                        ? 'Plant en begeleidt — geen eigen uitvoeringsuren.'
                        : 'Geen werkdag.')
                      : `Niets ingepland — ${fmt(c.capacityMinutes)} u beschikbaar. Sleep hier een taak naartoe.`}
                  </p>
                )}
                {mine.map((i) => {
                  const werf = A.werfLabel(i);
                  // WHO ELSE IS ON THIS JOB TODAY. A briefing card that names one person makes a duo
                  // look like a solo, which is exactly the arrangement this crew is planned around —
                  // and on a phone this card IS the whole calendar, so the co-worker has nowhere else
                  // to appear. Read off the same rows the lanes are built from; no extra request.
                  const withMe = items
                    .filter((o) => o.day === i.day && o.workpackageId === i.workpackageId
                                && o.resourceId !== i.resourceId)
                    .map((o) => o.resourceName).filter(Boolean);
                  return (
                    <article key={i.assignmentId} className={`job${isSelected(selection, i) ? ' selected' : ''}`}
                             {...(() => {
                               // ── TWO HANDLERS, ONE PROP ─────────────────────────
                               // `dragHandleProps` supplies its own `onPointerDown`, and a later
                               // `onPointerDown` in the same JSX REPLACES it rather than adding to
                               // it — which silently killed dragging in the day view the moment
                               // long-press was wired in. Chained explicitly, so both gestures start
                               // from the same press: the drag arms, and the long-press timer runs.
                               const dp = drag.dragHandleProps({
                                 workpackageId: i.workpackageId, projectId: i.projectId,
                                 assignmentId: i.assignmentId, resourceId: i.resourceId,
                                 title: i.title, planRevision: i.planRevision,
                                 plannedStart: i.plannedStart, plannedEnd: i.plannedEnd,
                                 day: i.day, allocationId: i.allocationId ?? null,
                                 minutes: i.minutes, allocatedMinutes: i.allocatedMinutes,
                               });
                               const down = dp.onPointerDown as ((e: React.PointerEvent) => void) | undefined;
                               return {
                                 ...dp,
                                 onPointerDown: (e: React.PointerEvent) => { down?.(e); chipPressStart(i, e); },
                                 onPointerUp: chipPressEnd,
                                 onPointerMove: chipPressMove,
                                 onPointerCancel: chipPressEnd,
                               };
                             })()}
                             onClick={(e) => chipClick(i, e)}
                             onDoubleClick={() => { setSelection({ keys: new Set([selKey(i)]), anchor: selKey(i) }); openItem(i); }}
                             onContextMenu={(e) => chipContext(i, e)}>
                      <div className="job-main">
                        <div className="werf-name">{werf.name}</div>
                        {/* THE ONE FIELD THAT IS EDITED IN PLACE. Renaming a task used to be six
                            actions and a round trip through the panel for a typo; here it is a
                            double-click and Enter. Everything else still goes through the panel,
                            because everything else is a decision rather than a correction. */}
                        <div className="job-task">
                          <InlineEdit value={i.title} ariaLabel="Taaknaam" className="job-title-edit"
                                      onCommit={(next) => run(async () => {
                                        await A.editWorkpackage(i.projectId, i.workpackageId,
                                          { title: next, correlationId: correlationId() });
                                        return { message: `Hernoemd naar "${next}".` };
                                      }, `"${i.title}"`)} />
                        </div>
                        <div className="faint">
                          {werf.code ? `${werf.code} · ` : ''}
                          {i.customerName ?? '—'}
                          {i.disciplineCode ? ` · ${A.disciplineLabel(i.disciplineCode)}` : ''}
                        </div>
                        {withMe.length > 0 && <div className="faint">samen met {withMe.join(', ')}</div>}
                        {i.scope && <p className="job-note">{i.scope}</p>}
                      </div>
                      <div className="job-side">
                        <strong>{hours(i.minutes)}</strong>
                        <span className="badge ro">{A.lifecycleLabel(i.lifecycleState)}</span>
                      </div>
                    </article>
                  );
                })}
                {c.otherMinutes > 0 && (
                  <p className="faint" style={{ margin: 0 }}>+ {hours(c.otherMinutes)} op een werf die je niet kan zien.</p>
                )}
              </section>
            );
          })}
        </div>
      )}

      {drag.state.payload && drag.state.at && (
        <div className="chip chip-float" style={{ left: drag.state.at.x + 12, top: drag.state.at.y + 12 }}>
          <span className="t">
            {drag.state.mode === 'move'
              ? (selected.length > 1 && selection.keys.has(`${drag.state.payload.workpackageId}:${drag.state.payload.resourceId}`)
                ? `${workpackages(selected).length} taken` : drag.state.payload.title)
              : `${drag.state.payload.title} — ${drag.state.mode === 'resize-end' ? 'einddatum' : 'startdatum'}`}
          </span>
          {drag.state.over && <span className="s">{longLabel(drag.state.over.day)}</span>}
        </div>
      )}

      {menu && (
        <ContextMenu at={menu} commands={COMMANDS} ctx={ctx} extra={[...splitExtra, ...menuExtra]}
                     onRun={(c) => void run(c)} onClose={() => setMenu(null)} />
      )}

      {/* ── WAAROM staat die cel op oranje ──────────────────────────────────────
          The breakdown behind one figure: every job that makes it up, plus the anonymous total from
          werven this planner cannot see. Without the last line an overload looks like a bug, because
          the visible jobs do not add up to the number. */}
      {why && (() => {
        const c = cap.at(why.resourceId, why.day);
        const jobs = items.filter((i) => i.day === why.day && i.resourceId === why.resourceId);
        return (
          <>
            <div className="scrim" onClick={() => setWhy(null)} aria-hidden />
            <div className="why" role="dialog" aria-label="Capaciteit">
              <header className="row">
                <strong>{why.name}</strong>
                <span className="faint">{longLabel(why.day)}</span>
                <span className="spacer" />
                <CapacityPill c={c} />
                <button className="btn small" onClick={() => setWhy(null)} aria-label="Sluiten">✕</button>
              </header>
              {c.nonWorking ? <p className="faint">Geen werkdag.</p> : (
                <>
                  <ul className="why-list">
                    {jobs.map((j) => (
                      <li key={j.assignmentId}>
                        <span>{A.werfLabel(j).name} · {j.title}</span>
                        <strong>{hours(j.minutes)}</strong>
                      </li>
                    ))}
                    {c.otherMinutes > 0 && (
                      <li className="faint">
                        <span>Werk op een werf die je niet kan zien</span>
                        <strong>{hours(c.otherMinutes)}</strong>
                      </li>
                    )}
                    {jobs.length === 0 && c.otherMinutes === 0 && <li className="faint">Nog niets ingepland.</li>}
                  </ul>
                  <p style={{ margin: 0 }}>
                    Samen <strong>{fmt(c.assignedMinutes)} u</strong> van {fmt(c.capacityMinutes)} u
                    {c.overloadMinutes > 0
                      ? <> — <strong className="over-text">{fmt(c.overloadMinutes)} u te veel</strong>.</>
                      : c.freeMinutes > 0 ? <> — nog {fmt(c.freeMinutes)} u vrij.</> : <> — precies vol.</>}
                  </p>
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* ── DE DAG OP EEN TELEFOON ────────────────────────────────────────────
          A bottom sheet rather than a view change: the planner keeps their place in the month, and
          closing it costs one tap instead of navigating back and losing the scroll position. */}
      {sheet && (() => {
        const dayItems = items.filter((i) => i.day === sheet);
        const c = cap.total(laneIds, [sheet]);
        return (
          <>
            <div className="scrim" onClick={() => setSheet(null)} aria-hidden />
            <div className="sheet" role="dialog" aria-label={`Dagdetail ${sheet}`}>
              <header className="row">
                <strong>{longLabel(sheet)}</strong>
                <span className="spacer" />
                <CapacityPill c={c} />
                <button className="btn small" onClick={() => setSheet(null)} aria-label="Sluiten">✕</button>
              </header>
              {dayItems.length === 0 && <p className="faint">Niets ingepland.</p>}
              <ul className="sheet-list">
                {dayItems.map((i) => {
                  const w = A.werfLabel(i);
                  return (
                    <li key={i.assignmentId}>
                      <button onClick={() => { setSheet(null); openItem(i); }}>
                        <span className="werf-name">{w.name}</span>
                        <span className="faint">{i.resourceName} · {i.title}</span>
                        <span className="faint">{w.code}</span>
                        <strong>{hours(i.minutes)}</strong>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button className="btn" onClick={() => { setAnchor(sheet); setView('day'); setSheet(null); }}>
                Open de dagweergave
              </button>
            </div>
          </>
        );
      })()}

      {splitting && (
        <SplitDialog target={splitting} people={people.data ?? []}
                     onClose={() => setSplitting(null)}
                     onDone={async (r) => {
                       await A.splitAllocation(splitting.projectId, {
                         assignmentId: splitting.assignmentId, day: splitting.day,
                         keepMinutes: r.keepMinutes, toDay: r.toDay,
                         toResourceId: r.toResourceId, screen: view, correlationId: correlationId(),
                       });
                       await qc.invalidateQueries({ queryKey: ['schedule'] });
                       setNotice({ kind: 'info', text:
                         `"${splitting.title}" opgesplitst: ${fmt(r.keepMinutes)} u blijft, ${fmt(r.splitMinutes)} u naar ${longLabel(r.toDay)}.` });
                     }} />
      )}

      {addingProject && (
        <AddProject onClose={() => setAddingProject(false)}
                    onAdded={(id) => { setProjectFilter(String(id)); }} />
      )}

      {editing && (
        <WorkpackagePanel subject={editing} people={people.data ?? []} projects={projects.data ?? []}
                          crew={crew.data ?? []} onClose={() => setEditing(null)} />
      )}

      {/* THE ONE BUTTON THAT WAS MISSING. Until now the only way work could exist was for somebody to
          have seeded it — a planner could move jobs around but never add one. */}
      <button className="fab" onClick={() => openNew()} aria-label="Nieuwe opdracht plannen" title="Nieuwe opdracht (n)">
        + Nieuwe opdracht
      </button>

      <p className="faint shortcuts">
        <strong>Sneltoetsen</strong> · <kbd>m</kbd>/<kbd>w</kbd>/<kbd>d</kbd> weergave · <kbd>t</kbd> vandaag ·
        <kbd>n</kbd> nieuw · <kbd>←</kbd><kbd>→</kbd> bladeren · klik selecteert, <kbd>⇧</kbd>klik en <kbd>⌘</kbd>klik
        selecteren meerdere · rechterklik opent het menu · <kbd>⌘</kbd><kbd>←</kbd>/<kbd>→</kbd> dag opschuiven ·
        <kbd>+</kbd>/<kbd>−</kbd> langer of korter · sleep de rand van een taak om ze te verlengen ·
        <kbd>⌘C</kbd>/<kbd>⌘V</kbd> kopiëren · <kbd>⌘Z</kbd> ongedaan maken
        {me.roles.length > 0 && <> · Aangemeld als {me.displayName} ({me.roles.join(', ')}).</>}
      </p>
    </div>
  );
}

/** How many chips a week cell shows before it collapses the rest into a count. */
const CELL_MAX = 3;

/** How many one-line entries a month cell shows before it offers to expand. */
const MONTH_ROWS = 6;

/** Icon-short labels for the selection bar, where the full sentence would not fit. */
const SHORT: Record<string, string> = {
  'day-earlier': '‹ dag', 'day-later': 'dag ›', 'next-week': 'volgende week',
  shorter: '− korter', longer: '+ langer', copy: 'Kopiëren', duplicate: 'Dupliceren',
  unassign: 'Weghalen',
};
